<template>
  <div :class="['score-meter-container', icon ? 'icon' : '']">
    <svg
      class="circle-two"
      :viewBox="`0 0 ${viewBoxSize} ${viewBoxSize}`"
    >
      <path
        fill="none"
        :stroke="grey400"
        stroke-linecap="round"
        :stroke-width="`${strokeWidth}`"
        :d="[
          `M${coloredArc1StartPointX},${coloredArc1StartPointY}`,
          `a${axisLength},${axisLength} 0 0,1`,
          `${center - coloredArc1StartPointX},${svgPadding - coloredArc1StartPointY}`,
        ].join(' ')"
      />
      <path
        fill="none"
        :stroke="grey400"
        stroke-linecap="round"
        :stroke-width="`${strokeWidth}`"
        :d="[
          `M${center},${svgPadding}`,
          `a${axisLength},${axisLength} 0 0,1`,
          `${center - coloredArc1StartPointX},${coloredArc1StartPointY - svgPadding}`,
        ].join(' ')"
      />
      <path
        v-show="coloredArc1Dx && coloredArc1Dy"
        fill="none"
        :stroke="`${color}`"
        stroke-linecap="round"
        :stroke-width="`${strokeWidth}`"
        :d="[
          `M${coloredArc1StartPointX},${coloredArc1StartPointY}`,
          `a${axisLength},${axisLength} 0 0,1`,
          `${coloredArc1Dx},${coloredArc1Dy}`,
        ].join(' ')"
      />
      <path
        v-show="coloredArc2Dx && coloredArc2Dy"
        fill="none"
        :stroke="`${color}`"
        stroke-linecap="round"
        :stroke-width="`${strokeWidth}`"
        :d="[
          `M${coloredArc2StartPointX},${coloredArc2StartPointY}`,
          `a${axisLength},${axisLength} 0 0,1`,
          `${coloredArc2Dx},${coloredArc2Dy}`,
        ].join(' ')"
      />
      <path
        v-if="!icon"
        :stroke="`${color}`"
        stroke-linecap="round"
        stroke-linejoin="round"
        :stroke-width="`${triangleStrokeWidth}`"
        :fill="`${color}`"
        :d="[
          `M${pathStartXTriangle},${pathStartYTriangle}`,
          `l ${dxToLeftPoint},${dyToLeftPoint}`,
          `${dxToRightPoint},${dyToRightPoint} z`,
        ].join(' ')"
      />
    </svg>
    <p :style="{ color }">
      {{ scoreToUse }}
    </p>
  </div>
</template>

<script>
import { useColors } from '@/composables/colors';

export default {
  props: {
    score: {
      type: [Number, String],
      default: null,
    },
    icon: {
      type: Boolean,
      default: false,
    },
    animate: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {
      grey400, basicRed, orange500, blue500, green500,
    } = useColors();

    return {
      grey400,
      basicRed,
      orange500,
      blue500,
      green500,
    };
  },
  data() {
    return {
      tweenedScore: 0,
      axisLength: 50,
      svgPadding: 5,
      startRadians: 1.28 * Math.PI,
      strokeWidth: 10,
      triangleDistanceFromCenterCoefficient: 0.775,
      triangleSideLength: 10,
      triangleStrokeWidth: 5,
    };
  },
  computed: {
    color() {
      if (this.scoreToUse === 0) {
        return this.grey400;
      }
      if (this.scoreToUse > 0 && this.scoreToUse <= 49) {
        return this.basicRed;
      }
      if (this.scoreToUse >= 50 && this.scoreToUse <= 69) {
        return this.orange500;
      }
      if (this.scoreToUse >= 70 && this.scoreToUse <= 84) {
        return this.blue500;
      }
      if (this.scoreToUse >= 85) {
        return this.green500;
      }
      return this.grey400;
    },
    center() {
      return this.svgPadding + this.axisLength;
    },
    viewBoxSize() {
      return this.center * 2;
    },
    coloredArc1StartPointX() {
      return this.center + Math.cos(this.startRadians) * this.axisLength;
    },
    coloredArc1StartPointY() {
      return this.center - Math.sin(this.startRadians) * this.axisLength;
    },
    coloredArc2StartPointX() {
      return this.center;
    },
    coloredArc2StartPointY() {
      // top of canvas
      return this.center - this.axisLength;
    },
    coloredArc1Dx() {
      if (this.scoreToUse >= 50) {
        return this.center - this.coloredArc1StartPointX;
      }
      return this.meterEndX - this.coloredArc1StartPointX;
    },
    coloredArc1Dy() {
      if (this.scoreToUse >= 50) {
        return this.svgPadding - this.coloredArc1StartPointY;
      }
      return this.meterEndY - this.coloredArc1StartPointY;
    },
    coloredArc2Dx() {
      if (this.scoreToUse <= 50) {
        return 0;
      }
      return this.meterEndX - this.coloredArc2StartPointX;
    },
    coloredArc2Dy() {
      if (this.scoreToUse <= 50) {
        return 0;
      }
      return this.meterEndY - this.coloredArc2StartPointY;
    },
    // Starts at point
    pathStartXTriangle() {
      const { meterEndCos, axisLength, triangleDistanceFromCenterCoefficient } = this;
      const distanceFromCenter = meterEndCos * axisLength * triangleDistanceFromCenterCoefficient;
      return this.center + distanceFromCenter;
    },
    pathStartYTriangle() {
      const { meterEndSin, axisLength, triangleDistanceFromCenterCoefficient } = this;
      const distanceFromCenter = meterEndSin * axisLength * triangleDistanceFromCenterCoefficient;
      return this.center - distanceFromCenter;
    },
    dxToLeftPoint() {
      return Math.cos(this.radiansMeterEnd + Math.PI * (5 / 6)) * this.triangleSideLength;
    },
    dyToLeftPoint() {
      return -(Math.sin(this.radiansMeterEnd + Math.PI * (5 / 6)) * this.triangleSideLength);
    },
    dxToRightPoint() {
      return Math.cos(this.radiansMeterEnd - Math.PI / 2) * this.triangleSideLength;
    },
    dyToRightPoint() {
      return -(Math.sin(this.radiansMeterEnd - Math.PI / 2) * this.triangleSideLength);
    },
    radiansToMeterEnd() {
      const cutoutSize = (1.5 * Math.PI - this.startRadians) * 2;
      const radiansTo100Percent = cutoutSize - 2 * Math.PI;
      const scoreAsPercentage = this.scoreToUse / 100;
      return scoreAsPercentage * radiansTo100Percent;
    },
    radiansMeterEnd() {
      return this.startRadians + this.radiansToMeterEnd;
    },
    meterEndCos() {
      return Math.cos(this.radiansMeterEnd);
    },
    meterEndSin() {
      return Math.sin(this.radiansMeterEnd);
    },
    meterEndX() {
      const distanceFromCenter = this.meterEndCos * this.axisLength;
      return this.center + distanceFromCenter;
    },
    meterEndY() {
      const distanceFromCenter = this.meterEndSin * this.axisLength;
      return this.center - distanceFromCenter;
    },
    scoreFormatted() {
      if (this.score === null || this.score === 0) {
        return 0;
      }
      return this.score;
    },
    scoreToUse() {
      if (this.icon && !this.animate) {
        return Math.round(this.score) || 0;
      }

      return Math.round(this.tweenedScore) || 0;
    },
  },
  watch: {
    scoreFormatted: {
      immediate: true,
      handler(newValue) {
        import('gsap').then(({ gsap }) => {
          gsap.to(this.$data, { duration: 0.5, tweenedScore: newValue });
        });
      },
    },
  },
};
</script>

<style scoped>
.score-meter-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.score-meter-container :deep(.circle-two) {
  position: absolute;
  width: 100%;
  height: 100%;
}

.score-meter-container :deep(p) {
  position: absolute;
  top: 2%;
  left: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 20px;
  height: 100%;
  width: 100%;
}
.score-meter-container.icon :deep(p) {
  top: 0%;
  font-size: 16px;
}
</style>
